import {ColorOverlayFilter} from 'pixi-filters';
import {Stack} from '../pixi_scene/game_scene/game_table/table_objects/stacks/Stack';
import {Filter, Rectangle} from 'pixi.js';
import {IPoint} from '../interfaces/primitives/IPoint';
import PixiMessenger from '../../api/render/pixi/PixiMessenger';
import {StaticData} from '../data/StaticData';
import {IRectangle} from '../interfaces/primitives/IRectangle';

export class StackSelector
{
    static selectedStacks: Stack[] = [];
    static stashedStacks: Stack[] = [];
    private static highlightFilter: ColorOverlayFilter = new ColorOverlayFilter(0xffff00, .3);
    private static hightlightedStack: Stack | null;
    private static hoveredStack: Stack | null;

    /**
     * Подсветить колоду
     * @param stack - колода которую нужно подсветить
     */
    static highlight(stack: Stack): void {
        StackSelector.unhighlight();
        StackSelector.tryAddFilter(stack, StackSelector.highlightFilter, true);
        StackSelector.hightlightedStack = stack;
    }

    /**
     * Убрать подсветку с текущей подсвеченной колоды
     */
    static unhighlight(): void {
        StackSelector.hightlightedStack && StackSelector.tryRemoveFilter(StackSelector.hightlightedStack, StackSelector.highlightFilter);
        StackSelector.hightlightedStack = null;
    }

    static hoverStack(stack: Stack): void {
        StackSelector.hoveredStack = stack;
    }

    static unhoverStack(): void {
        StackSelector.hoveredStack = null;
    }

    static hovered(): Stack | null {
        return StackSelector.hoveredStack;
    }

    static highlighted(): Stack | null {
        return StackSelector.hightlightedStack;
    }

    /**
     * Выбрать указанную колоду
     * @param stack - колода которую нужно выбрать
     * @param currentOnly - выбрать только ёё, если false - добавляет колоду к выбранным
     */
    static selectStack(stack: Stack, currentOnly: boolean = true): void {
        currentOnly && StackSelector.unselectAll();
        if (!StackSelector.selected(stack)) {
            StackSelector.selectedStacks.push(stack);
            PixiMessenger.sendDisableCardMessage([stack.activeData.id], true);
        }
    }

    /**
     * Снять выбор со всех выбранных колод
     */
    static unselectAll(silently: boolean = false): void {
        let stack: Stack;
        while (StackSelector.selectedStacks.length) {
            stack = StackSelector.selectedStacks.shift()!;
            if (silently) {
                StackSelector.tryRemoveFilter(stack, stack.usedByPlayerFilter);
            } else {
                PixiMessenger.sendEnableCardMessage([stack.activeData.id]);
            }
        }
        this.unhighlight();
    }

    /**
     * Проверить, является ли данная колода одной из выбранных
     * @param stack - колода которую нужно проверить
     */
    static selected(stack: Stack): boolean {
        return StackSelector.selectedStacks.indexOf(stack) > -1;
    }

    /**
     * Получить Прямоугольник, описанный вокруг выделенных колод
     */
    static getSelectedStacksRectangle(): Rectangle {
        let left: number = Number.MAX_VALUE;
        let right: number = -Number.MAX_VALUE;
        let top: number = Number.MAX_VALUE;
        let bottom: number = -Number.MAX_VALUE;
        for (let stack of StackSelector.selectedStacks) {
            left = Math.min(left, stack.points.leftTop.x, stack.points.rightTop.x, stack.points.leftBottom.x, stack.points.rightBottom.x);
            right = Math.max(right, stack.points.leftTop.x, stack.points.rightTop.x, stack.points.leftBottom.x, stack.points.rightBottom.x);
            top = Math.min(top, stack.points.leftTop.y, stack.points.rightTop.y, stack.points.leftBottom.y, stack.points.rightBottom.y);
            bottom = Math.max(bottom, stack.points.leftTop.y, stack.points.rightTop.y, stack.points.leftBottom.y, stack.points.rightBottom.y);
        }
        return new Rectangle(left, top, right - left, bottom - top);
    }

    /**
     * Переместить выделенные колоды на указанную дельту по вертикали и горизонтали
     * @param moveDelta объект, содеращий дельты перемещений по осям
     */
    static moveSelectedOnBy(moveDelta: IPoint): void {
        for (let stack of StackSelector.selectedStacks) {
            stack.moveTo({x: stack.x + moveDelta.x, y: stack.y + moveDelta.y});
        }
    }

    /**
     * Убрать выделение с указанной колоды
     * @param stack - колода, которую нужно удалить
     */
    static unselectStack(stack: Stack): void {
        if (!StackSelector.selected(stack)) {
            return;
        }
        StackSelector.selectedStacks.splice(StackSelector.selectedStacks.indexOf(stack), 1);
        PixiMessenger.sendEnableCardMessage([stack.activeData.id]);
    }

    static getHotkeyStacks(): Stack[] {
        let returning: Stack[] = [];
        if (StackSelector.selectedStacks.length) {
            returning = this.selectedStacks;
        } else if (this.hoveredStack && (!this.hoveredStack.activeData.blockedByPlayer || this.hoveredStack.activeData.blockedByPlayer == StaticData.playerId)) {
            return [this.hoveredStack];
        }
        returning = returning.filter(stack => !stack.activeData.locked && (!stack.activeData.blockedByPlayer || stack.activeData.blockedByPlayer == StaticData.playerId));
        return returning;
    }

    static toggleSelected(stack: Stack): void {
        if (StackSelector.selected(stack)) {
            StackSelector.unselectStack(stack);
        } else {
            StackSelector.selectStack(stack, false);
        }
    }

    /**
     * Применяем фильтр к колоде
     * @param stack - колода к которой применяем фильтр
     * @param filter - применяемый к данной колоде фильтр
     * @param toEnd - флаг указывающий на очередность фильтра при добавлении, елси true - фильтр добавтся в конец массива фильтров объекта
     */
    static tryAddFilter(stack: Stack, filter: Filter, toEnd: boolean = false): void {
        if (stack.filters?.length) {
            let index: number = stack.filters.indexOf(filter);
            if (index == -1) {
                toEnd ? stack.filters.push(filter) : stack.filters.unshift(filter);
            }
        } else {
            stack.filters = [filter];
        }
    }

    /**
     * Убираем фильтр с колоды
     * @param stack - колода с которой уюираем фильтр
     * @param filter - убираемый с данной колоды фильтр
     */
    static tryRemoveFilter(stack: Stack, filter: Filter): void {
        if (stack.filters?.length) {
            let index: number;
            while ((index = stack.filters.indexOf(filter)) > -1) {
                stack.filters.splice(index, 1);
            }
        }
    }

    /**
     * Добавить фильтры к колодам
     * @param stacks - колоды к которым нужно добавить фильтры
     */
    static tryAddFilters(stacks: Stack[]): void {
        stacks.forEach(stack => {
            StackSelector.tryAddFilter(stack, stack.usedByPlayerFilter);
        });
    }

    /**
     * Запомнить выделенные колоды
     */
    static stash(): void {
        StackSelector.stashedStacks = StackSelector.selectedStacks.concat();
    }

    /**
     * Восстановить выделенные колоды из запомненных
     */
    static unstash(): void {
        StackSelector.selectedStacks = StackSelector.stashedStacks;
        StackSelector.clearStash();
    }

    /**
     * Убрать фильтры из колод
     * @param stacks - колоды из которых нужно убрать фильтры
     */
    static tryRemoveFilters(stacks: Stack[]): void {
        stacks.forEach(stack => {
            StackSelector.tryRemoveFilter(stack, stack.usedByPlayerFilter);
        });
    }

    /**
     * Очистить запомненные выделенные колоды
     */
    static clearStash(): void {
        StackSelector.stashedStacks = [];
    }

    /**
     * Снять выделение с запомненных выделенных колод
     */
    static unselectStashed(): void {
        StackSelector.stashedStacks.forEach(stack => {
            PixiMessenger.sendEnableCardMessage([stack.activeData.id]);
        });
    }

    /**
     * Выделить все колоды в прямоугольнке
     * @param stacks - колоды среди которых происходит проверка на попадание хотябы одного угла в прямоугольник (учитывая вращение)
     * @param rect - прямоугольник, на попадание в который происходит проверка колод
     */
    static selectStacksInRect(stacks: Stack[], rect: IRectangle): void {
        stacks.forEach(stack => {
            if (stack.hasPointInRect(rect)) {
                StackSelector.selected(stack) || stack.activeData.locked || stack.activeData.usedByPlayer || StackSelector.selectStack(stack, false);
            } else {
                StackSelector.selected(stack) && StackSelector.unselectStack(stack);
            }
        });
    }
}
